import { Injectable, NgZone } from '@angular/core';
import Ajv from 'ajv';
import addFormats from 'ajv-formats';

import { AttributeName } from '../interfaces';

import { IField, ISchema, InputFieldType, IValidationResult, IFieldWithInputElement, IObjectWithId } from './interfaces';
import { UtilsService } from './utils.service';

@Injectable({
    providedIn: 'root'
})
export class ValidatorService {
    private readonly dummyObjectId = '000000000000000000000000';
    private fields: IField[] = [];
    private participant: IObjectWithId = null;
    private contactFields: IField[] = [];
    private ajvInstance: Ajv;
    private participantFieldValidatedCallback:
        (fieldId: string, value: any, validationResult: IValidationResult, errorsContainerElementId?: string) => void;
    private contactFieldValidatedCallback:
        (fieldId: string, value: any, validationResult: IValidationResult, errorsContainerElementId?: string) => void;
    private subParticipantFieldValidatedCallback:
        (fieldId: string, value: any, validationResult: IValidationResult, element: HTMLElement, errorsContainerElementId?: string) => void;
    private multiRegistrationTicketFieldValidatedCallback:
        (fieldId: string, validationResult: IValidationResult, element: HTMLElement, errorsContainerElementId?: string) => void;

    constructor(private utilsSvc: UtilsService, private readonly ngZone: NgZone) {
        this.ajvInstance = new Ajv();
        addFormats(this.ajvInstance);
        this.setGlobalValidationFunctions();
    }

    getEmailValidationErrorText(email: string): string {
        const isEmailValid = this.ajvInstance.validate({ type: 'string', format: 'email' }, email);
        if (isEmailValid) {
            return null;
        }
        return this.ajvInstance.errorsText(this.ajvInstance.errors);
    }

    setParticipantFieldValidatedCallback(
        callback: (fieldId: string, element: HTMLElement, validationResult: IValidationResult, errorsContainerElementId?: string) => void
    ): void {
        this.participantFieldValidatedCallback = callback;
    }

    setContactFieldValidatedCallback(
        callback: (fieldId: string, value: any, validationResult: IValidationResult, errorsContainerElementId?: string) => void
    ): void {
        this.contactFieldValidatedCallback = callback;
    }

    setSubParticipantValidatedCallback(
        callback: ValidatorService['subParticipantFieldValidatedCallback']
    ): void {
        this.subParticipantFieldValidatedCallback = callback;
    }

    setMultiRegistrationTicketFieldValidatedCallback(
        callback: ValidatorService['multiRegistrationTicketFieldValidatedCallback']
    ): void {
        this.multiRegistrationTicketFieldValidatedCallback = callback;
    }

    setFields(fields: IField[], participant: IObjectWithId): void {
        this.fields = fields;
        this.participant = participant;
    }

    setContactFields(contactFields: IField[]): void {
        this.contactFields = contactFields;
    }

    validateAllFields(fieldWithInputElements: IFieldWithInputElement[]): void {
        for (const item of fieldWithInputElements) {
            const validationResult = this.validateParticipantField(item.field.id, item.element);
            if (this.participantFieldValidatedCallback) {
                // TODO: If 2 or more same fields exist, they will have suffixes after
                //       the id like '...field-validation-errors-2' etc.
                const errorsContainerElementId = `${item.field.id}-field-validation-errors`;
                this.ngZone.run(() => this.participantFieldValidatedCallback(
                    item.field.id, item.element, validationResult, errorsContainerElementId
                ));
            }
        }
    }

    validateContactField(fieldId: string, inpElement: HTMLElement): IValidationResult | null {
        const field = this.contactFields?.find(x => x.id === fieldId);
        if (!field) {
            return null;
        }
        return this.validateField(field, inpElement);
    }

    validateParticipantField(fieldId: string, inpElement: HTMLElement): IValidationResult | null {
        const field = this.fields.find(x => x.id === fieldId);
        if (!field) {
            return null;
        }
        return this.validateField(field, inpElement);
    }

    getFieldValueFromParticipantObject(fieldId: string): any {
        return this.participant ? this.participant[fieldId] : undefined;
    }

    validateField(field: IField, inpElement: HTMLElement): IValidationResult | null {
        const fieldType = this.utilsSvc.getFieldTypeFromField(field);
        let value: any;
        let isDummyDocumentValue = false;
        let isDummyImageValue = false;
        if (fieldType === 'image') {
            value = this.getFieldValueFromParticipantObject(field.id);
            if (!value) {
                // There is no value for this image field in the participant
                // Check if the user just provided a value
                // Find the image element and check its "src" property
                const imgElement: HTMLImageElement = inpElement.querySelector(`[${AttributeName.azaImageFieldImgElement}]`);
                if (imgElement) {
                    // Check the "src" attribute - if it has a value then image is set
                    // We don't check the "src" property, because it will have a value (equal to "baseURI") even if the <img> element does not have image
                    if (imgElement.getAttribute('src')) {
                        value = this.dummyObjectId;
                        isDummyImageValue = true;
                    }
                }
            }
        } else if (fieldType === 'document') {
            value = this.getFieldValueFromParticipantObject(field.id);
            if (!value) {
                // There is no value for this document field in the participant
                // Check if the user just provided a value
                // When the first time the page is loaded, inpElement is the wrapper element, not the real input element
                // This is because the wrapper element has the specific attribute "field-id", not the input element
                // This is because in the case of document fields, the input element does not contain the value
                // But if the user selects a file, then it is the input element itself
                const isDocumentInputElement = inpElement.hasAttribute(AttributeName.azaDocumentFieldInputField);
                if (isDocumentInputElement) {
                    // This is the document input element
                    const inputElement = inpElement as HTMLInputElement;
                    if (inputElement?.files?.length === 1) {
                        // The user has selected some file
                        // We have to provide valid id value (because we still don't have document id from the server)
                        // so the validation succeeds
                        value = this.dummyObjectId;
                        isDummyDocumentValue = true;
                    }
                }
            }
        } else {
            value = this.utilsSvc.getInputElementValue(field, inpElement);
        }
        const setRequiredClassResult = this.setRequiredClass(inpElement, value);

        if (setRequiredClassResult.requiredAdded) {
            return {
                isValid: false, isRequiredButNotProvided: true, value: value
            } as IValidationResult;
        }
        if (!setRequiredClassResult.isRequired && !value) {
            // This input field is not required and it does not have a value
            // Consider it as valid and do not validate it against the schema
            return {
                isValid: true, isRequiredButNotProvided: false, value: value
            } as IValidationResult;
        }
        const validationResult = this.validate(field.schema, value, fieldType);
        validationResult.value = value;
        return validationResult;
    }

    getFieldById(fieldId: string): IField | undefined {
        return this.fields.find(x => x.id === fieldId);
    }

    setRequiredClass(el: HTMLElement, value: any): { requiredAdded: boolean, isRequired: boolean } {
        let azaInputRequiredEl = this.getAzaInputRequiredParent(el);
        if (!azaInputRequiredEl) {
            // Parent with the input required attribute is not found, try with this element itself
            if (el.hasAttribute(AttributeName.azaInputRequired)) {
                azaInputRequiredEl = el;
            }
        }
        if (!azaInputRequiredEl) {
            return { requiredAdded: false, isRequired: false };
        }
        const requiredValue = azaInputRequiredEl.getAttribute(AttributeName.azaInputRequired);
        // If we have the attribute aza-input-required without value or with value true - the field is considered required
        const isRequired = requiredValue === '' || requiredValue === 'true';
        let requiredAdded: boolean;
        if (!isRequired) {
            // Not required - remove the required class
            azaInputRequiredEl.classList.remove(AttributeName.azaInputRequired);
            requiredAdded = false;
        } else {
            if (Array.isArray(value)) {
                if (value.length === 0) {
                    azaInputRequiredEl.classList.add(AttributeName.azaInputRequired);
                    requiredAdded = true;

                } else {
                    azaInputRequiredEl.classList.remove(AttributeName.azaInputRequired);
                    requiredAdded = false;
                }
            } else {
                if (!value && value !== 0) {
                    // Falsy values are null, undefined, empty strings but not 0 - 0 is valid value
                    azaInputRequiredEl.classList.add(AttributeName.azaInputRequired);
                    requiredAdded = true;
                } else {
                    azaInputRequiredEl.classList.remove(AttributeName.azaInputRequired);
                    requiredAdded = false;
                }
            }
        }
        return { requiredAdded: requiredAdded, isRequired: isRequired };
    }

    getAzaInputRequiredParent(childEl: HTMLElement): HTMLElement {
        let currentParrentEl = childEl.parentElement;
        while (currentParrentEl) {
            if (currentParrentEl.hasAttribute(AttributeName.azaInputRequired)) {
                return currentParrentEl;
            }
            currentParrentEl = currentParrentEl.parentElement;
        }
        return null;
    }

    private validate(schema: ISchema, data: any, type: InputFieldType): IValidationResult {
        const result = {} as IValidationResult;
        if (type === 'number' || type === 'integer') {
            // ajv successfully validates NaN and +/-Infinity so deal with them by ourselves
            if (isNaN(data) || !isFinite(data)) {
                // The value is NaN or +/-Infinity
                result.isValid = false;
                result.errorsText = 'Not a valid numeric value';
                return result;
            }
        }
        result.isValid = this.ajvInstance.validate(schema, data) as boolean;
        if (!result.isValid) {
            if (type === 'email'
                && this.ajvInstance.errors
                && this.ajvInstance.errors[0]
                && this.ajvInstance.errors[0].keyword === 'pattern') {
                // Email fields can have complex patterns excluding specific email domains
                // If the error is that the value does not conform to the email pattern
                // We will consider this as not allowed domain and will change the error text
                result.errorsText = 'This email domain is not allowed';
            } else {
                result.errorsText = this.ajvInstance.errorsText();
            }
        }
        return result;
    }

    private setGlobalValidationFunctions(): void {
        // Expose functionality to global scope
        const azavista: any = globalThis.azavista || {};
        azavista.validateField = (fieldId: string, element: HTMLElement, errorsContainerElementId?: string) => {
            const validationResult = this.validateParticipantField(fieldId, element);
            if (this.participantFieldValidatedCallback) {
                this.ngZone.run(() => this.participantFieldValidatedCallback(fieldId, element, validationResult, errorsContainerElementId));
            }
        };
        azavista.validateSubParticipantField = (fieldId: string, element: HTMLElement, errorsContainerElementId?: string) => {
            const validationResult = this.validateParticipantField(fieldId, element);
            if (this.subParticipantFieldValidatedCallback) {
                this.ngZone.run(() => this.subParticipantFieldValidatedCallback(fieldId, element, validationResult, element, errorsContainerElementId));
            }
            if (this.participantFieldValidatedCallback) {
                this.ngZone.run(() => this.participantFieldValidatedCallback(fieldId, element, validationResult, errorsContainerElementId));
            }
        };
        azavista.validateMultiRegistrationTicketField = (fieldId: string, element: HTMLElement, errorsContainerElementId?: string) => {
            const validationResult = this.validateField({
                name: fieldId,
                id: fieldId,
                schema: {type: 'string'}
            } as IField, element);
            if (this.multiRegistrationTicketFieldValidatedCallback) {
                this.ngZone.run(() => this.multiRegistrationTicketFieldValidatedCallback(fieldId, validationResult, element, errorsContainerElementId));
            }
        };
        azavista.validateContactField = (fieldId: string, element: HTMLElement, errorsContainerElementId?: string) => {
            const validationResult = this.validateContactField(fieldId, element);
            if (this.contactFieldValidatedCallback) {
                this.ngZone.run(() => this.contactFieldValidatedCallback(fieldId, element, validationResult, errorsContainerElementId));
            }
        };
        globalThis.azavista = azavista;
    }
}
